/**
 * E2EE Service
 * Handles encryption/decryption and session management
 * 
 * Note: This is a simplified implementation. For production, you should use
 * a proper cryptographic library like libsignal-protocol-javascript or
 * implement the full Double Ratchet protocol.
 */

import {
  getSessionState,
  saveSessionState,
  hasE2EEKeys,
  getRegistrationId,
  saveRegistrationId,
  getDeviceId,
  saveDeviceId,
} from "../utils/e2eeStorage";
import {
  initializeUserKeysAPI,
  getUserKeyBundleAPI,
  initializeE2EESessionAPI,
  getE2EESessionStatusAPI,
} from "../api/e2eeApi";

/**
 * Check if E2EE is enabled and initialized for the current user
 * @returns {boolean}
 */
export const isE2EEEnabled = () => {
  return hasE2EEKeys();
};

/**
 * Initialize E2EE keys for the current user
 * This should be called once when the user first enables E2EE
 * @returns {Promise<{success: boolean, data: {registrationId: number, deviceId: number}}>}
 */
export const initializeE2EE = async () => {
  try {
    // Check if already initialized
    if (hasE2EEKeys()) {
      return {
        success: true,
        message: "E2EE already initialized",
        data: {
          registrationId: getRegistrationId(),
          deviceId: getDeviceId(),
        },
      };
    }

    // Initialize keys on server
    const response = await initializeUserKeysAPI();

    if (response.success) {
      // Store registration ID and device ID
      saveRegistrationId(response.data.registrationId);
      saveDeviceId(response.data.deviceId);

      // Note: In a real implementation, you would also store the keys
      // returned from the server (identity key, signed prekey, one-time prekeys)
      // The server should return these keys encrypted or you should generate
      // them on the client side
    }

    return response;
  } catch (error) {
    console.error("Error initializing E2EE:", error);
    throw error;
  }
};

/**
 * Check if a session exists for a conversation
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<{hasSession: boolean, isInitialized: boolean}>}
 */
export const checkSessionStatus = async (conversationId) => {
  try {
    // First check local storage
    const localSession = getSessionState(conversationId);
    if (localSession) {
      return {
        hasSession: true,
        isInitialized: true,
      };
    }

    // Check server
    const response = await getE2EESessionStatusAPI(conversationId);
    return {
      hasSession: response.data?.hasSession || false,
      isInitialized: response.data?.isInitialized || false,
    };
  } catch (error) {
    console.error("Error checking session status:", error);
    return {
      hasSession: false,
      isInitialized: false,
    };
  }
};

/**
 * Initialize E2EE session with another user
 * @param {string} recipientId - The recipient user ID
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<{success: boolean, sessionId: string}>}
 */
export const initializeSession = async (recipientId, conversationId) => {
  try {
    // Check if session already exists
    const sessionStatus = await checkSessionStatus(conversationId);
    if (sessionStatus.hasSession && sessionStatus.isInitialized) {
      return {
        success: true,
        message: "Session already exists",
        sessionId: getSessionState(conversationId)?.sessionId,
      };
    }

    // Get recipient's key bundle
    const keyBundleResponse = await getUserKeyBundleAPI(recipientId);
    if (!keyBundleResponse.success) {
      throw new Error("Failed to get recipient key bundle");
    }

    const keyBundle = keyBundleResponse.data;

    // Initialize session on server
    const sessionResponse = await initializeE2EESessionAPI(
      recipientId,
      conversationId,
      keyBundle
    );

    if (sessionResponse.success) {
      // Save session state locally
      // Note: In a real implementation, you would derive the session state
      // from the X3DH key agreement protocol
      const sessionState = {
        sessionId: sessionResponse.data.sessionId,
        conversationId: conversationId,
        recipientId: recipientId,
        initializedAt: new Date().toISOString(),
        // Add other session state properties as needed
      };

      saveSessionState(conversationId, sessionState);
    }

    return sessionResponse;
  } catch (error) {
    console.error("Error initializing session:", error);
    throw error;
  }
};

/**
 * Ensure E2EE session exists before sending a message
 * @param {string} recipientId - The recipient user ID
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<boolean>} - Returns true if session is ready
 */
export const ensureSessionExists = async (recipientId, conversationId) => {
  try {
    // Check if E2EE is enabled
    if (!isE2EEEnabled()) {
      // Initialize E2EE if not already done
      await initializeE2EE();
    }

    // Check session status
    const sessionStatus = await checkSessionStatus(conversationId);

    if (!sessionStatus.hasSession || !sessionStatus.isInitialized) {
      // Initialize session
      await initializeSession(recipientId, conversationId);
    }

    return true;
  } catch (error) {
    console.error("Error ensuring session exists:", error);
    return false;
  }
};

/**
 * Encrypt a message (simplified - in production use proper Double Ratchet)
 * @param {string} message - The message to encrypt
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<{encryptedContent: string, messageNumber: number}>}
 */
export const encryptMessage = async (message, conversationId) => {
  try {
    const sessionState = getSessionState(conversationId);
    if (!sessionState) {
      throw new Error("No session state found for conversation");
    }

    // Note: This is a placeholder. In a real implementation, you would:
    // 1. Derive a message key from the chain key using Double Ratchet
    // 2. Encrypt the message using AES-256-GCM
    // 3. Increment the message number
    // 4. Update the session state

    // For now, we'll return a placeholder that indicates encryption should happen on server
    // The actual encryption should be done on the server side using the session state
    return {
      encryptedContent: message, // Placeholder - server will encrypt
      messageNumber: sessionState.messageNumber || 0,
      sessionId: sessionState.sessionId,
    };
  } catch (error) {
    console.error("Error encrypting message:", error);
    throw error;
  }
};

/**
 * Decrypt a message (simplified - in production use proper Double Ratchet)
 * @param {string} encryptedContent - The encrypted message (base64)
 * @param {number} messageNumber - The message number
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<string>} - The decrypted message
 */
export const decryptMessage = async (encryptedContent, messageNumber, conversationId) => {
  try {
    const sessionState = getSessionState(conversationId);
    if (!sessionState) {
      throw new Error("No session state found for conversation");
    }

    // Note: This is a placeholder. In a real implementation, you would:
    // 1. Derive the message key from the chain key using Double Ratchet
    // 2. Decrypt the message using AES-256-GCM
    // 3. Handle out-of-order messages by storing previous chain keys
    // 4. Update the session state

    // TODO: Implement proper decryption using Double Ratchet protocol
    // For now, since decryption is not yet implemented, we return the encrypted content
    // The backend should handle decryption, or we need to implement it here

    if (!encryptedContent) {
      throw new Error("No encrypted content provided");
    }

    // Since full decryption is not yet implemented, we'll return the encrypted content
    // In production, this should decrypt using the session state and Double Ratchet protocol
    // For now, return as-is - the backend should send decrypted content in the content field
    // and encrypted content in encryptedContent field

    console.warn("Decryption not yet implemented - returning encrypted content", {
      conversationId,
      messageNumber,
      sessionId: sessionState.sessionId
    });

    // Return encrypted content as-is until decryption is implemented
    // The UI should handle showing this appropriately
    return encryptedContent;
  } catch (error) {
    console.error("Error decrypting message:", error);
    throw error;
  }
};

/**
 * Process received encrypted message
 * @param {object} message - The message object with isEncrypted, encryptedContent, messageNumber
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<string>} - The decrypted message content
 */
export const processEncryptedMessage = async (message, conversationId) => {
  if (!message.isEncrypted) {
    return message.content; // Not encrypted, return as-is
  }

  try {
    // Check if content and encryptedContent are the same (backend bug)
    // If they are the same, it means backend put encrypted data in content field
    if (message.content === message.encryptedContent && message.encryptedContent) {
      // Both fields contain encrypted data - this is a backend bug
      // Since decryption is not yet implemented, we'll skip processing
      // and let the backend handle it, or show a simple indicator
      console.warn("Backend bug: content and encryptedContent are the same (both encrypted). Decryption not yet implemented.");
      // Don't try to decrypt - just return a simple indicator
      // In production, this should be decrypted on the backend before sending
      return "[🔒 Encrypted]";
    }

    // If content is different from encryptedContent, check if content is already decrypted
    // (backend might have decrypted it already)
    if (message.content && message.content !== message.encryptedContent) {
      // Content looks like it might be decrypted already
      // Check if it's not base64-like (encrypted data)
      const isContentBase64 = /^[A-Za-z0-9+/=]+$/.test(message.content) &&
        message.content.length > 16;

      if (!isContentBase64) {
        // Content doesn't look encrypted, use it
        return message.content;
      }
    }

    // If we have encryptedContent, try to decrypt it
    if (message.encryptedContent) {
      const decrypted = await decryptMessage(
        message.encryptedContent,
        message.messageNumber,
        conversationId
      );
      return decrypted;
    }

    // Fallback: return content as-is
    return message.content || "[🔒 Encrypted]";
  } catch (error) {
    console.error("Error processing encrypted message:", error);
    // Return a simple indicator instead of showing encrypted content
    return "[🔒 Encrypted]";
  }
};

